import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiChevronDown,
  FiClock,
  FiEdit3,
  FiMinus,
  FiPlus,
  FiSave,
  FiX,
} from 'react-icons/fi';
import { ref, update } from 'firebase/database';
import { db } from '../../firebaseConfig';
import { getCachedFirebaseValue, subscribeCachedValue } from '../firebaseDataCache';
import { refreshNetworkingRecommendations } from '../goalsApi';
import { useLanguage } from '../contexts/LanguageContext';

const GOAL_META = {
  applications: {
    title: 'Job applications',
    goalTitle: 'Application goal',
    color: '#568DED',
    unit: 'applications',
    targetUnit: 'job applications',
    logLabel: 'Log application',
  },
  interviews: {
    title: 'Practice interviews',
    goalTitle: 'Interview goal',
    color: '#40EB6E',
    unit: 'interviews',
    targetUnit: 'practice interviews',
    logLabel: 'Log interview',
  },
  networking: {
    title: 'Networking chats',
    goalTitle: 'Networking goal',
    color: '#CC40EB',
    unit: 'chats',
    targetUnit: 'networking chats',
    logLabel: 'Log chat',
  },
};

const DEFAULT_DRAFT = {
  applications: { enabled: false, target: 5, period: 'weekly' },
  interviews: { enabled: false, target: 1, period: 'weekly' },
  networking: { enabled: false, target: 3, period: 'weekly' },
};

const DEFAULT_GOALS = {
  applications: { enabled: false, target: 5, period: 'weekly', current: 0, byDay: {} },
  interviews: { enabled: false, target: 1, period: 'weekly', current: 0 },
  networking: { enabled: false, target: 3, period: 'weekly', current: 0, suggestions: [], suggestionsGeneratedAt: 0 },
};
export const HISTORY_DISPLAY_LIMIT = 8;

function nowMs() {
  return Date.now();
}

function todayKey() {
  return new Date(nowMs()).toISOString().slice(0, 10);
}

function currentWeekStart() {
  const today = new Date(nowMs());
  const day = today.getUTCDay() || 7;
  const monday = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - day + 1));
  return monday.toISOString().slice(0, 10);
}

function cleanTarget(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.min(99, parsed));
}

function normalizeGoals(goals = {}) {
  return Object.entries(DEFAULT_GOALS).reduce((next, [key, defaults]) => {
    const goal = goals[key] || {};
    next[key] = {
      ...defaults,
      ...goal,
      enabled: Boolean(goal.enabled),
      target: cleanTarget(goal.target, defaults.target),
      period: key === 'applications' && goal.period === 'daily' ? 'daily' : 'weekly',
      current: Math.max(0, Number.parseInt(goal.current || 0, 10) || 0),
    };
    if (key === 'applications') {
      next[key].byDay = goal.byDay && typeof goal.byDay === 'object' ? goal.byDay : {};
    }
    if (key === 'networking') {
      next[key].suggestions = Array.isArray(goal.suggestions) ? goal.suggestions : [];
    }
    return next;
  }, {});
}

function normalizeDraft(goals = {}) {
  return Object.entries(DEFAULT_DRAFT).reduce((next, [key, defaults]) => {
    const goal = goals[key] || {};
    next[key] = {
      enabled: Boolean(goal.enabled),
      target: Number(goal.target) || defaults.target,
      period: key === 'applications' && goal.period === 'daily' ? 'daily' : 'weekly',
    };
    return next;
  }, {});
}

function parseWeekDate(value) {
  if (!value) return '';
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatWeekRange(value, language = 'en') {
  const start = parseWeekDate(value);
  if (!start) return value || '';
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const locale = language === 'ar' ? 'ar-LB' : 'en-US';
  const startMonth = start.toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' });
  const endMonth = end.toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' });
  const number = new Intl.NumberFormat(locale);
  const startDay = number.format(start.getUTCDate());
  const endDay = number.format(end.getUTCDate());
  return startMonth === endMonth
    ? `${startMonth} ${startDay}–${endDay}`
    : `${startMonth} ${startDay}–${endMonth} ${endDay}`;
}

function formatWeekDeadline(value, language = 'en') {
  const start = parseWeekDate(value);
  if (!start) return '';
  start.setUTCDate(start.getUTCDate() + 6);
  return start.toLocaleDateString(language === 'ar' ? 'ar-LB' : 'en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
}

export function goalPeriodLabel(goal, language = 'en') {
  if (language === 'ar') return goal?.period === 'daily' ? 'اليوم' : 'هذا الأسبوع';
  return goal?.period === 'daily' ? 'Today' : 'This week';
}

function formatTemplate(template, values) {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

function targetCopy(goal, meta, weekStart, language, translate) {
  const locale = language === 'ar' ? 'ar-LB' : 'en-US';
  const target = new Intl.NumberFormat(locale).format(goal?.target || 0);
  if (goal?.period === 'daily') return formatTemplate(translate('completeToday'), { target, unit: meta.targetUnit });
  const deadline = formatWeekDeadline(weekStart, language);
  return formatTemplate(translate(deadline ? 'completeBy' : 'completeWeek'), { target, unit: meta.targetUnit, date: deadline });
}

function goalDisplayCurrent(goalType, goal) {
  if (goalType === 'applications' && goal.period === 'daily') {
    return Number((goal.byDay || {})[todayKey()] || 0);
  }
  return Number(goal.current || 0);
}

export function buildGoalProgressPatch(goalType, goal, amount, day = todayKey()) {
  if (!goal?.enabled) return null;
  const direction = amount < 0 ? -1 : 1;
  const displayedCurrent = goalType === 'applications' && goal.period === 'daily'
    ? Number((goal.byDay || {})[day] || 0)
    : Number(goal.current || 0);
  if (direction < 0 && displayedCurrent <= 0) return null;

  const patch = {
    [`goals/${goalType}/current`]: Math.max(0, Number(goal.current || 0) + direction),
  };
  if (goalType === 'applications') {
    const dayCurrent = Number((goal.byDay || {})[day] || 0);
    if (direction > 0 || dayCurrent > 0) {
      patch[`goals/applications/byDay/${day}`] = Math.max(0, dayCurrent + direction);
    }
  }
  return patch;
}

function summarizeGoals(goals) {
  return Object.entries(goals).reduce((next, [goalType, goal]) => {
    const target = cleanTarget(goal.target, 1);
    const current = goalDisplayCurrent(goalType, goal);
    next[goalType] = {
      enabled: Boolean(goal.enabled),
      target,
      period: goal.period,
      current,
      weeklyTotal: Number(goal.current || 0),
      progress: target ? Math.min(100, Math.round((current / target) * 100)) : 0,
      complete: current >= target,
    };
    return next;
  }, {});
}

function historyRecord(state, closedAt) {
  const goals = normalizeGoals(state.goals);
  const summary = summarizeGoals(goals);
  const enabled = Object.values(summary).filter((goal) => goal.enabled);
  const completionRate = enabled.length
    ? Math.round(enabled.reduce((total, goal) => total + Math.min(1, goal.current / goal.target), 0) / enabled.length * 100)
    : 0;
  return {
    weekStart: state.currentWeekStart || currentWeekStart(),
    closedAt,
    goals: summary,
    completionRate,
  };
}

function resetGoalCounts(goals) {
  const reset = normalizeGoals(goals);
  Object.entries(reset).forEach(([goalType, goal]) => {
    goal.current = 0;
    if (goalType === 'applications') goal.byDay = {};
  });
  return reset;
}

export function serializeGoalState(raw = {}) {
  const goals = normalizeGoals(raw.goals);
  const history = raw.history && typeof raw.history === 'object' ? raw.history : {};
  return {
    currentWeekStart: raw.currentWeekStart || currentWeekStart(),
    goals,
    summary: summarizeGoals(goals),
    hasActiveGoals: Object.values(goals).some((goal) => goal.enabled),
    history: Object.values(history)
      .sort((left, right) => String(right.weekStart || '').localeCompare(String(left.weekStart || '')))
      .slice(0, HISTORY_DISPLAY_LIMIT),
  };
}

function looksClipped(value) {
  const text = String(value || '').trim();
  if (text.length < 170) return false;
  return !/[.!?)]$/.test(text);
}

function hasClippedSuggestions(goals = {}) {
  const suggestions = goals.networking?.suggestions || [];
  return suggestions.some((item) => looksClipped(item.reason) || looksClipped(item.outreachAngle));
}

export function initialCareerGoalUpdates(raw = {}) {
  const weekStart = currentWeekStart();
  const existingWeek = raw.currentWeekStart;
  const goals = normalizeGoals(raw.goals);
  const createdAt = raw.createdAt || nowMs();

  if (!existingWeek) {
    return {
      currentWeekStart: weekStart,
      goals,
      createdAt,
      updatedAt: nowMs(),
    };
  }

  if (existingWeek !== weekStart) {
    const closedAt = nowMs();
    return {
      currentWeekStart: weekStart,
      goals: resetGoalCounts(goals),
      [`history/${existingWeek}`]: historyRecord({ ...raw, goals }, closedAt),
      lastResetAt: closedAt,
      updatedAt: closedAt,
    };
  }

  return null;
}

function GoalsTooltipButton({ icon: Icon, label, children }) {
  const rootRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState(null);

  const placeTooltip = () => {
    const trigger = rootRef.current?.querySelector('.js-goals-tooltip-btn');
    if (!trigger || typeof window === 'undefined') return;

    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 8;
    const width = Math.min(300, window.innerWidth - (viewportPadding * 2));
    const roomBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
    const roomAbove = rect.top - gap - viewportPadding;
    const placeBelow = roomBelow >= 260 || roomBelow >= roomAbove;
    const maxHeight = Math.max(140, placeBelow ? roomBelow : roomAbove);
    const rtl = document.documentElement.dir === 'rtl';
    const preferredLeft = rtl ? rect.left : rect.right - width;
    const left = Math.min(
      Math.max(preferredLeft, viewportPadding),
      window.innerWidth - width - viewportPadding,
    );

    setTooltipStyle({
      left,
      width,
      maxHeight,
      ...(placeBelow
        ? { top: rect.bottom + gap, bottom: 'auto' }
        : { top: 'auto', bottom: window.innerHeight - rect.top + gap }),
    });
  };

  const closeTooltip = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setTooltipStyle(null);
  };

  return (
    <span
      className="js-goals-action-tip"
      ref={rootRef}
      onMouseEnter={placeTooltip}
      onMouseLeave={closeTooltip}
      onFocusCapture={placeTooltip}
      onBlurCapture={closeTooltip}
    >
      <button type="button" className="js-goals-tooltip-btn" aria-label={label} title={label}>
        <Icon aria-hidden="true" />
        <span>{label}</span>
      </button>
      <span className="js-goals-tooltip" role="tooltip" style={tooltipStyle || undefined}>
        {children}
      </span>
    </span>
  );
}

function ActiveGoalsPanel({ user, compact = false }) {
  const { language, t } = useLanguage();
  const tg = (key) => t(`activeGoals.${key}`);
  const number = (value) => new Intl.NumberFormat(language === 'ar' ? 'ar-LB' : 'en-US').format(value || 0);
  const localizedGoalMeta = useMemo(() => Object.entries(GOAL_META).reduce((result, [key, meta]) => ({
    ...result,
    [key]: {
      ...meta,
      title: tg(`types.${key}.title`),
      goalTitle: tg(`types.${key}.goalTitle`),
      unit: tg(`types.${key}.unit`),
      targetUnit: tg(`types.${key}.targetUnit`),
      logLabel: tg(`types.${key}.logLabel`),
    },
  }), {}), [language]); // eslint-disable-line react-hooks/exhaustive-deps
  const careerGoalsPath = user?.uid ? `people/${user.uid}/careerGoals` : '';
  const cachedGoalRaw = careerGoalsPath ? getCachedFirebaseValue(careerGoalsPath) : undefined;
  const initialGoalState = cachedGoalRaw !== undefined ? serializeGoalState(cachedGoalRaw || {}) : null;
  const [goalState, setGoalState] = useState(initialGoalState);
  const [draft, setDraft] = useState(() => (
    initialGoalState ? normalizeDraft(initialGoalState.goals) : DEFAULT_DRAFT
  ));
  const [editing, setEditing] = useState(false);
  const [networkingOpen, setNetworkingOpen] = useState(false);
  const [busy, setBusy] = useState(user?.uid && cachedGoalRaw === undefined ? 'load' : '');
  const [error, setError] = useState('');
  const repairAttemptedRef = useRef(false);

  useEffect(() => {
    if (!user?.uid) {
      setGoalState(null);
      return undefined;
    }

    setBusy('load');
    const careerGoalsRef = ref(db, careerGoalsPath);
    const handler = (snapshot) => {
      const raw = snapshot.val() || {};
      const setupUpdates = initialCareerGoalUpdates(raw);
      if (setupUpdates) {
        update(careerGoalsRef, setupUpdates).catch((err) => setError(err.message));
      }
      const payload = serializeGoalState(setupUpdates ? { ...raw, ...setupUpdates } : raw);
      setGoalState(payload);
      setDraft(normalizeDraft(payload.goals));
      setBusy((current) => (current === 'load' ? '' : current));
    };
    return subscribeCachedValue(careerGoalsRef, careerGoalsPath, handler, (err) => {
      setError(err.message);
      setBusy('');
    });
  }, [user?.uid, careerGoalsPath]);

  const activeGoals = useMemo(() => {
    const goals = goalState?.goals || {};
    const summary = goalState?.summary || {};
    return Object.keys(localizedGoalMeta)
      .map((key) => ({
        key,
        meta: localizedGoalMeta[key],
        goal: goals[key] || {},
        summary: summary[key] || {},
      }))
      .filter((item) => item.goal.enabled);
  }, [goalState, localizedGoalMeta]);

  const updateDraft = (goalType, patch) => {
    setDraft((current) => ({
      ...current,
      [goalType]: {
        ...current[goalType],
        ...patch,
      },
    }));
  };

  const saveGoals = async () => {
    setBusy('save');
    setError('');
    try {
      const currentGoals = normalizeGoals(goalState?.goals);
      const nextGoals = Object.entries(currentGoals).reduce((next, [goalType, goal]) => {
        next[goalType] = {
          ...goal,
          enabled: Boolean(draft[goalType].enabled),
          target: cleanTarget(draft[goalType].target, DEFAULT_GOALS[goalType].target),
          period: goalType === 'applications' && draft[goalType].period === 'daily' ? 'daily' : 'weekly',
        };
        return next;
      }, {});
      await update(ref(db, `people/${user.uid}/careerGoals`), {
        currentWeekStart: goalState?.currentWeekStart || currentWeekStart(),
        goals: nextGoals,
        updatedAt: nowMs(),
      });
      setEditing(false);
      if (nextGoals.networking.enabled && nextGoals.networking.suggestions.length === 0) {
        refreshNetworkingRecommendations({ user }).catch(() => {});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const changeProgress = async (goalType, amount) => {
    setBusy(`${amount < 0 ? 'decrement' : 'increment'}-${goalType}`);
    setError('');
    try {
      const goals = normalizeGoals(goalState?.goals);
      const goal = goals[goalType];
      const progressPatch = buildGoalProgressPatch(goalType, goal, amount);
      if (!progressPatch) return;
      const eventId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const updates = {
        ...progressPatch,
        [`events/${eventId}`]: {
          id: eventId,
          goalType,
          amount: amount < 0 ? -1 : 1,
          source: 'manual',
          createdAt: nowMs(),
        },
        updatedAt: nowMs(),
      };
      await update(ref(db, `people/${user.uid}/careerGoals`), updates);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const cancelEdit = () => {
    setDraft(normalizeDraft(goalState?.goals));
    setEditing(false);
  };

  const isLoading = busy === 'load' && !goalState;

  useEffect(() => {
    if (!user?.uid || repairAttemptedRef.current || !goalState?.goals?.networking?.enabled) return;
    if (!hasClippedSuggestions(goalState.goals)) return;
    repairAttemptedRef.current = true;
    refreshNetworkingRecommendations({ user }).catch((err) => setError(err.message));
  }, [goalState, user]);

  return (
    <section className={`js-goals-panel ${compact ? 'is-compact' : ''}`} aria-labelledby="active-goals-title">
      <div className="js-goals-panel__head">
        <div>
          <h2 id="active-goals-title" className="js-section-title">{tg('title')}</h2>
          {goalState?.currentWeekStart && <p>{formatWeekRange(goalState.currentWeekStart, language)}</p>}
        </div>
        <div className="js-goals-panel__actions">
          {!editing && Array.isArray(goalState?.history) && goalState.history.length > 0 && (
            <GoalsTooltipButton icon={FiClock} label={tg('history')}>
              <strong>{tg('weeklyHistory')}</strong>
              {goalState.history.slice(0, HISTORY_DISPLAY_LIMIT).map((week) => (
                <span className="js-goals-history__row" key={week.weekStart}>
                  <span>{formatWeekRange(week.weekStart, language)}</span>
                  <span className="js-goals-history__bar">
                    <i style={{ width: `${Math.min(week.completionRate || 0, 100)}%` }} />
                  </span>
                  <b>{number(week.completionRate)}%</b>
                </span>
              ))}
            </GoalsTooltipButton>
          )}
          {editing ? (
            <>
              <button type="button" className="js-goals-icon-btn" onClick={saveGoals} disabled={Boolean(busy)}>
                <FiSave aria-hidden="true" />
                <span>{tg('save')}</span>
              </button>
              <button type="button" className="js-goals-icon-btn" onClick={cancelEdit} disabled={Boolean(busy)}>
                <FiX aria-hidden="true" />
                <span>{tg('cancel')}</span>
              </button>
            </>
          ) : (
            <button type="button" className="js-goals-icon-btn" onClick={() => setEditing(true)}>
              <FiEdit3 aria-hidden="true" />
              <span>{tg('edit')}</span>
            </button>
          )}
        </div>
      </div>

      {error && <p className="js-goals-error">{error}</p>}
      {isLoading && <div className="js-personnel-spinner" />}

      {!isLoading && editing && (
        <div className="js-goals-editor">
          {Object.entries(localizedGoalMeta).map(([goalType, meta]) => (
            <div className="js-goals-editor__row" key={goalType}>
              <label className="js-goals-toggle">
                <input
                  type="checkbox"
                  checked={draft[goalType].enabled}
                  onChange={(event) => updateDraft(goalType, { enabled: event.target.checked })}
                />
                <span>{meta.title}</span>
              </label>
              <input
                type="number"
                min="1"
                max="99"
                value={draft[goalType].target}
                onChange={(event) => updateDraft(goalType, { target: event.target.value })}
                aria-label={formatTemplate(tg('target'), { title: meta.title })}
              />
              {goalType === 'applications' ? (
                <select
                  value={draft.applications.period}
                  onChange={(event) => updateDraft('applications', { period: event.target.value })}
                  aria-label={tg('applicationPeriod')}
                >
                  <option value="daily">{tg('perDay')}</option>
                  <option value="weekly">{tg('perWeek')}</option>
                </select>
              ) : (
                <span className="js-goals-period">{tg('perWeek')}</span>
              )}
            </div>
          ))}

        </div>
      )}

      {!isLoading && !editing && activeGoals.length === 0 && (
        <div className="js-goals-empty">
          <p>{tg('empty')}</p>
          <button type="button" className="js-goals-primary" onClick={() => setEditing(true)}>
            {tg('setGoals')}
          </button>
        </div>
      )}

      {!isLoading && !editing && activeGoals.length > 0 && (
        <div className="js-active-goals__list">
          {activeGoals.map(({ key, meta, goal, summary }) => (
            <article className="js-goal-meter" key={key}>
              <div className="js-goal-meter__head">
                <div>
                  <p className="js-goal-meter__title">{meta.goalTitle}</p>
                  <small>{targetCopy(goal, meta, goalState.currentWeekStart, language, tg)}</small>
                </div>
                <div className="js-goal-meter__actions">
                  <button
                    type="button"
                    className="js-goal-meter__undo"
                    onClick={() => changeProgress(key, -1)}
                    disabled={Boolean(busy) || Number(summary.current || 0) <= 0}
                    aria-label={formatTemplate(tg('undo'), { unit: meta.unit })}
                    title={formatTemplate(tg('undo'), { unit: meta.unit })}
                  >
                    <FiMinus aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="js-goal-meter__log"
                    onClick={() => changeProgress(key, 1)}
                    disabled={Boolean(busy)}
                  >
                    <FiPlus aria-hidden="true" />
                    <span>{meta.logLabel}</span>
                  </button>
                </div>
              </div>
              <div className="js-goal-meter__row">
                <div className="js-goal-meter__track">
                  <div
                    className="js-goal-meter__fill"
                    style={{ width: `${Math.min(summary.progress || 0, 100)}%`, background: meta.color }}
                  />
                </div>
                <span className="js-goal-meter__value">
                  {formatTemplate(tg('progress'), {
                    current: number(summary.current),
                    target: number(summary.target || goal.target),
                    unit: meta.unit,
                  })}
                </span>
              </div>
              {goal.period === 'daily' && Number(summary.weeklyTotal || 0) > 0 && (
                <p className="js-goal-meter__period">{formatTemplate(tg('weekTotal'), { count: number(summary.weeklyTotal) })}</p>
              )}
              {!compact && key === 'networking' && Array.isArray(goal.suggestions) && goal.suggestions.length > 0 && (
                <div className="js-networking-suggestions">
                  <button
                    type="button"
                    className="js-networking-suggestions__head"
                    onClick={() => setNetworkingOpen((open) => !open)}
                    aria-expanded={networkingOpen}
                  >
                    <span>{tg('networkingRecommended')}</span>
                    <FiChevronDown className="js-icon--directional-down" aria-hidden="true" />
                  </button>
                  {networkingOpen && (
                    <div className="js-networking-suggestions__list">
                      {goal.suggestions.slice(0, 3).map((item) => (
                        <div className="js-networking-suggestion" key={`${item.title}-${item.reason}`}>
                          <strong dir="auto">{item.title}</strong>
                          {item.reason && <p dir="auto">{item.reason}</p>}
                          {item.outreachAngle && <p dir="auto">{item.outreachAngle}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

    </section>
  );
}

export default ActiveGoalsPanel;
